import { Navigate, useNavigate } from '@tanstack/react-router'
import type { InvestorData, MembershipData, UserData } from '@workspace/api-backend'
import { createContext, type ReactNode, useCallback, useContext } from 'react'
import { useInvestorCreditBalanceQuery, useInvestorProfileQuery } from '../hooks/api'
import { useBasePath } from '../hooks/use-base-path'
import { useAppContext } from './app-guard'

// ─── Types ───────────────────────────────────────────────────────────────────

export interface InvestorContextValue {
  user: UserData
  investor: InvestorData
  investors: MembershipData[]
  creditBalance: number | undefined
  switchInvestor: (investorId: string) => void
}

// ─── Context ─────────────────────────────────────────────────────────────────

const InvestorContext = createContext<InvestorContextValue | null>(null)

export function useInvestorContext() {
  const ctx = useContext(InvestorContext)
  if (!ctx) throw new Error('useInvestorContext must be used within /app/investor/$id')
  return ctx
}

// ─── Provider (handles loading + role guard) ─────────────────────────────────

export function InvestorRouteGuard({
  investorId,
  children,
}: {
  investorId: string
  children: ReactNode
}) {
  const { user, memberships } = useAppContext()
  const {
    data: investor,
    isPending: isInvestorPending,
    isError: isInvestorError,
  } = useInvestorProfileQuery(investorId)
  const { data: creditBalance } = useInvestorCreditBalanceQuery(investorId)
  const navigate = useNavigate()
  const basePath = useBasePath()

  const investors = memberships.filter((m) => m.type === 'investor')

  const switchInvestor = useCallback(
    (newInvestorId: string) => {
      const newPath = basePath.replace(/\/app\/investor\/[^/]+/, `/app/investor/${newInvestorId}`)
      navigate({ to: newPath })
    },
    [basePath, navigate]
  )

  if (isInvestorPending) return null

  if (user.userType !== 'INVESTOR') {
    return <Navigate to="/app" />
  }

  if (isInvestorError || !investor) {
    const firstInvestor = investors[0]
    if (firstInvestor) {
      return <Navigate to="/app/investor/$id" params={{ id: firstInvestor.id }} />
    }
    return <Navigate to="/app" />
  }

  return (
    <InvestorContext.Provider value={{ user, investor, investors, creditBalance, switchInvestor }}>
      {children}
    </InvestorContext.Provider>
  )
}
